import { useEffect, useMemo, useRef, useState } from "react";

export default function useHibidUsers({
  orgReady,
  tab,
  userId,
  subscriptionId,
  needsSubscriptionLink,
  apiBaseUrl,
  routes,
  apiFetchJson,
  handleNeedsAuth,
  toastOpen,
  setSyncInfo,
  readCache,
  writeCache,
  clearCache,
  usersCacheKey,
  usersCachePrefix,
  usersFetchSig,
  usersCacheTtlMs,
  usersPageSize,
  userTableRowHeight,
  userTableOverscanRows,
  normalize,
  normalizeTierKey,
  normalizeCustomerTypeKey,
  normalizeAccountManagerId,
  isEmail,
  safeToMillis,
  safeNum,
  toAbsInt,
  formatNumber2,
  waitForNextPaint,
  accountManagerTierMap,
}) {
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [newTierSubFilter, setNewTierSubFilter] = useState("new_to_af");
  const [regsThresholdFilter, setRegsThresholdFilter] = useState("all");
  const [lastEmailFilter, setLastEmailFilter] = useState("all");
  const [customerTypeFilter, setCustomerTypeFilter] = useState("all");
  const [accountManagerFilter, setAccountManagerFilter] = useState("all");
  const [userRows, setUserRows] = useState([]);
  const [, setUserTotalCount] = useState(null);
  const userTableContainerRef = useRef(null);
  const userTableScrollRafRef = useRef(0);
  const userTablePendingScrollTopRef = useRef(0);
  const [userTableScrollTop, setUserTableScrollTop] = useState(0);
  const [userTableViewportHeight, setUserTableViewportHeight] = useState(0);
  const [userSelected, setUserSelected] = useState(() => new Set());
  const [userSortKey, setUserSortKey] = useState("updatedAt");
  const [userSortDir, setUserSortDir] = useState("desc");
  const [userCursorDocId, setUserCursorDocId] = useState("");
  const [userHasMore, setUserHasMore] = useState(false);
  const [userLoadingMore, setUserLoadingMore] = useState(false);
  const [usersLoadedOnce, setUsersLoadedOnce] = useState(false);
  const [usersInitialLoading, setUsersInitialLoading] = useState(true);
  const [usersTableLoading, setUsersTableLoading] = useState(false);
  const [customerTypeSavingByEmail, setCustomerTypeSavingByEmail] = useState({});
  const [accountManagerSavingByEmail, setAccountManagerSavingByEmail] = useState({});
  const usersSearchInputRef = useRef(null);
  const usersRequestSeqRef = useRef(0);
  const activeUsersResetRequestRef = useRef(0);
  const activeUsersLoadMoreRequestRef = useRef(0);

  const getBuyerTier = (row = {}) => row?.buyerTierManual ?? row?.buyerTier ?? row?.tier ?? row?.derived?.tier ?? "";
  const getRegsCount = (row = {}) =>
    toAbsInt(
      row?.myAuctionsRegistrationCountValue ??
      row?.myAuctionsRegistrationCount ??
      row?.regs ??
      row?.derived?.registrationsCount ??
      0
    );
  const getBidsCount = (row = {}) =>
    toAbsInt(
      row?.myAuctionsUniqueBidCountValue ??
      row?.myAuctionsUniqueBidCount ??
      row?.bids ??
      0
    );
  const getHibidBids90Count = (row = {}) =>
    toAbsInt(
      row?.rolling?.win90?.totalBidsPlaced ??
      row?.win90?.totalBidsPlaced ??
      row?.derived?.hibidBids90 ??
      0
    );
  const getBidIntensity90 = (row = {}) =>
    safeNum(
      row?.rolling?.win90?.bidIntensity ??
      row?.win90?.bidIntensity ??
      row?.derived?.bidIntensity90 ??
      0
    );
  const getBpi90 = (row = {}) =>
    safeNum(
      row?.derived?.bpi90 ??
      row?.derived?.resellerPotentialScore90 ??
      row?.bpi?.d90 ??
      0
    );
  const getRegs12mCount = (row = {}) =>
    toAbsInt(
      row?.totalRegistrationsLast12MonthsValue ??
      row?.totalRegistrationsLast12Months ??
      row?.regs12m ??
      0
    );
  const getLcvEstimate = (row = {}) => {
    const value = row?.lcv?.estimate ?? row?.lcvEstimate ?? row?.derived?.lcvEstimate ?? null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const getOverallHammerPrice = (row = {}) => {
    const value =
      row?.overallHammerPriceValue ??
      row?.overallHammerPrice ??
      row?.totalHighBid ??
      row?.derived?.overallHammerPriceValue ??
      null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const getEffectiveTier = (row = {}) => {
    const manualTier = normalizeTierKey(row?.buyerTierManual || "");
    if (manualTier) return manualTier;
    return getRegsCount(row) === 1 ? "New" : getBuyerTier(row);
  };
  const getCustomerType = (row = {}) =>
    normalizeCustomerTypeKey(row?.customerTypeManual ?? row?.customerType ?? row?.derived?.customerType ?? "");
  const getRollingWin = (row = {}, key) => row?.[key] ?? row?.rolling?.[key] ?? null;
  const getLotsWonFromWin = (win) => {
    const numeric = Number(win?.lotsWon ?? null);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const getResolvedAccountManagerIdForUser = (row = {}) => {
    const explicitId = normalizeAccountManagerId(row?.accountManagerId || "");
    if (explicitId) return explicitId;
    const tierKey = normalizeTierKey(getEffectiveTier(row));
    return normalizeAccountManagerId(accountManagerTierMap?.[tierKey] || "");
  };
  const getUserEmailLower = (row = {}) => normalize(row?.email || row?.id);

  const invalidateUsersCache = () => {
    try {
      if (!usersCachePrefix) return;
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(usersCachePrefix)) keys.push(key);
      }
      keys.forEach((key) => clearCache(key));
    } catch {}
    activeUsersResetRequestRef.current = 0;
    activeUsersLoadMoreRequestRef.current = 0;
    setUsersTableLoading(false);
    setUsersLoadedOnce(false);
    setUsersInitialLoading(true);
  };

  const applyUserPatchToCachedPages = (email, patchOrUpdater) => {
    const normalizedEmail = normalize(email);
    if (!normalizedEmail || !usersCachePrefix) return;
    try {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(usersCachePrefix)) keys.push(key);
      }
      keys.forEach((key) => {
        const cached = readCache(key, { allowStale: true });
        if (!cached || !Array.isArray(cached.rows)) return;
        let changed = false;
        const nextRows = cached.rows.map((user) => {
          const rowEmail = normalize(user?.email || user?.id);
          if (rowEmail !== normalizedEmail) return user;
          changed = true;
          return typeof patchOrUpdater === "function"
            ? patchOrUpdater(user)
            : { ...user, ...(patchOrUpdater || {}) };
        });
        if (!changed) return;
        writeCache(key, {
          ...cached,
          rows: nextRows,
        });
      });
    } catch {}
  };

  const applyUserPatchLocally = (email, patchOrUpdater) => {
    const normalizedEmail = normalize(email);
    if (!normalizedEmail) return;
    setUserRows((prev) =>
      (prev || []).map((user) => {
        const rowEmail = normalize(user?.email || user?.id);
        if (rowEmail !== normalizedEmail) return user;
        return typeof patchOrUpdater === "function"
          ? patchOrUpdater(user)
          : { ...user, ...(patchOrUpdater || {}) };
      })
    );
    applyUserPatchToCachedPages(normalizedEmail, patchOrUpdater);
  };

  const applyUsersSearch = () => {
    const next = String(usersSearchInputRef.current?.value || "").trim();
    setSearch(next);
  };

  const clearUsersSearch = () => {
    if (usersSearchInputRef.current) usersSearchInputRef.current.value = "";
    setSearch("");
  };

  const buildUsersQueryParams = ({
    cursorDocId = "",
    sync = false,
    includeTotalCount = false,
    recalcOverallHammer = false,
  } = {}) => {
    const qs = new URLSearchParams();
    qs.set("sync", sync ? "true" : "false");
    qs.set("limit", String(usersPageSize));
    qs.set("includeTotalCount", includeTotalCount ? "true" : "false");
    if (recalcOverallHammer) qs.set("recalcOverallHammer", "true");
    if (cursorDocId) qs.set("cursorDocId", String(cursorDocId || ""));
    return qs;
  };

  const mergeUsersUnique = (prevRows, nextRows) => {
    const prev = Array.isArray(prevRows) ? prevRows : [];
    const next = Array.isArray(nextRows) ? nextRows : [];
    const seen = new Set(prev.map((row) => normalize(row?.email || row?.id)));
    const output = [...prev];
    next.forEach((row) => {
      const email = normalize(row?.email || row?.id);
      if (!email || seen.has(email)) return;
      seen.add(email);
      output.push(row);
    });
    return output;
  };

  const fetchUsers = async ({ force = false, sync = false, reset = true, recalcOverallHammer = false } = {}) => {
    if (!orgReady) return;
    const cursorDocId = reset ? "" : String(userCursorDocId || "").trim();
    if (!reset && (!userHasMore || !cursorDocId)) return;
    if (reset && (activeUsersResetRequestRef.current || usersTableLoading) && !force) return;
    if (!reset && (activeUsersLoadMoreRequestRef.current || userLoadingMore)) return;

    const requestId = (usersRequestSeqRef.current += 1);
    if (reset) {
      activeUsersResetRequestRef.current = requestId;
      setUsersInitialLoading(true);
      setUsersTableLoading(true);
    } else {
      activeUsersLoadMoreRequestRef.current = requestId;
      setUserLoadingMore(true);
    }

    try {
      const pageCacheKey = usersCacheKey(usersFetchSig, cursorDocId);
      if (!force && !sync) {
        const cached = readCache(pageCacheKey, { ttlMs: usersCacheTtlMs });
        if (cached && Array.isArray(cached.rows)) {
          const cachedIsPartial = !!cached?.hasMore && Boolean(cached?.nextCursorDocId);
          if (!(reset && cachedIsPartial)) {
            if (reset) setUserRows(cached.rows);
            else setUserRows((prev) => mergeUsersUnique(prev, cached.rows));
            if (cached?.totalUsersCount !== undefined) {
              if (cached.totalUsersCount === null) {
                setUserTotalCount(null);
              } else {
                const numeric = Number(cached.totalUsersCount);
                setUserTotalCount(Number.isFinite(numeric) ? Math.max(0, Math.trunc(numeric)) : null);
              }
            }
            setUserCursorDocId(String(cached?.nextCursorDocId || ""));
            setUserHasMore(!!cached?.hasMore && Boolean(cached?.nextCursorDocId));
            if (reset) setUserSelected(new Set());
            if (reset) setUsersLoadedOnce(true);
            if (cached?.sync !== undefined) setSyncInfo(cached?.sync ?? null);
            if (reset) await waitForNextPaint();
            return;
          }
        }
      }

      const seenCursors = new Set();
      const MAX_AUTO_PAGES = 1000;
      let fetchCursor = cursorDocId;
      let includeTotalCount = reset;
      let fetchedRows = [];
      let totalUsersCount = null;
      let latestSync = null;
      let finalNextCursor = "";
      let finalHasMore = false;
      let pageCount = 0;

      while (pageCount < MAX_AUTO_PAGES) {
        pageCount += 1;
        const qs = buildUsersQueryParams({
          cursorDocId: fetchCursor,
          sync,
          includeTotalCount,
          recalcOverallHammer: reset && recalcOverallHammer,
        });
        const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.users}?${qs.toString()}`);
        if (requestId !== usersRequestSeqRef.current) return;
        if (handleNeedsAuth(res, data)) {
          if (reset) {
            setSyncInfo(null);
            setUserRows([]);
            setUserTotalCount(null);
            setUserCursorDocId("");
            setUserHasMore(false);
            setUserSelected(new Set());
            setUsersLoadedOnce(false);
          }
          return;
        }
        if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);

        const rows = Array.isArray(data.rows) ? data.rows : [];
        fetchedRows = mergeUsersUnique(fetchedRows, rows);

        if (includeTotalCount) {
          const hasTotalUsersCount = data?.totalUsersCount !== null && data?.totalUsersCount !== undefined;
          const totalUsersRaw = hasTotalUsersCount ? Number(data?.totalUsersCount) : NaN;
          totalUsersCount = Number.isFinite(totalUsersRaw) ? Math.max(0, Math.trunc(totalUsersRaw)) : null;
        }

        latestSync = data?.sync ?? latestSync;
        finalNextCursor = String(data?.nextCursorDocId || "");
        finalHasMore = !!data?.hasMore && Boolean(finalNextCursor);

        if (!finalHasMore) break;
        if (!finalNextCursor || seenCursors.has(finalNextCursor)) break;
        seenCursors.add(finalNextCursor);
        fetchCursor = finalNextCursor;
        includeTotalCount = false;
      }

      if (reset) {
        setUserRows(fetchedRows);
        setUserSelected(new Set());
        setUsersLoadedOnce(true);
      } else {
        setUserRows((prev) => mergeUsersUnique(prev, fetchedRows));
      }
      if (totalUsersCount != null) setUserTotalCount(totalUsersCount);
      else if (reset) setUserTotalCount(null);
      setUserCursorDocId(finalHasMore ? finalNextCursor : "");
      setUserHasMore(finalHasMore && Boolean(finalNextCursor));
      setSyncInfo(latestSync ?? null);

      writeCache(pageCacheKey, {
        rows: fetchedRows,
        totalUsersCount,
        nextCursorDocId: finalHasMore ? finalNextCursor : "",
        hasMore: finalHasMore && Boolean(finalNextCursor),
        sync: latestSync ?? null,
      });
      if (reset) await waitForNextPaint();
    } catch (error) {
      toastOpen(error?.message || "Failed to load users", "error");
    } finally {
      if (reset) {
        if (activeUsersResetRequestRef.current === requestId) {
          activeUsersResetRequestRef.current = 0;
          setUsersInitialLoading(false);
          setUsersTableLoading(false);
        }
      } else if (activeUsersLoadMoreRequestRef.current === requestId) {
        activeUsersLoadMoreRequestRef.current = 0;
        setUserLoadingMore(false);
      }
    }
  };

  const loadMoreUsers = async () => {
    if (!orgReady || tab !== 0) return;
    if (usersTableLoading || userLoadingMore || activeUsersLoadMoreRequestRef.current) return;
    if (!userHasMore) return;
    await fetchUsers({ force: false, sync: false, reset: false });
  };

  const reloadUsersFromServer = async () => {
    invalidateUsersCache();
    await fetchUsers({ force: true, sync: false, reset: true, recalcOverallHammer: true });
  };

  const onUsersTableScroll = (evt) => {
    const element = evt?.currentTarget;
    if (!element) return;
    userTablePendingScrollTopRef.current = Number(element.scrollTop || 0);
    if (!userTableScrollRafRef.current) {
      userTableScrollRafRef.current = window.requestAnimationFrame(() => {
        userTableScrollRafRef.current = 0;
        setUserTableScrollTop(userTablePendingScrollTopRef.current);
      });
    }
    if (element.scrollHeight - element.scrollTop - element.clientHeight <= 220) {
      void loadMoreUsers();
    }
  };

  useEffect(() => {
    if (!orgReady) return;
    if (tab !== 0) return;
    if (usersLoadedOnce) return;
    fetchUsers({ force: false, sync: false, reset: true });
  }, [orgReady, tab, usersLoadedOnce]);

  useEffect(() => {
    if (!usersLoadedOnce) return;
    setUsersInitialLoading(false);
  }, [usersLoadedOnce]);

  useEffect(() => {
    setUserRows([]);
    setUserCursorDocId("");
    setUserHasMore(false);
    setUserSelected(new Set());
    setUsersLoadedOnce(false);
    setUsersInitialLoading(true);
    activeUsersResetRequestRef.current = 0;
    activeUsersLoadMoreRequestRef.current = 0;
    setUsersTableLoading(false);
  }, [userId, subscriptionId]);

  useEffect(() => {
    if (tab !== 0) return;
    setUserTableScrollTop(0);
    const element = userTableContainerRef.current;
    if (element) element.scrollTop = 0;
  }, [tab, search, tierFilter, newTierSubFilter, regsThresholdFilter, lastEmailFilter, customerTypeFilter, accountManagerFilter, userSortKey, userSortDir]);

  useEffect(() => {
    if (tab !== 0) return undefined;
    const element = userTableContainerRef.current;
    if (!element) return undefined;
    const syncViewportHeight = () => setUserTableViewportHeight(Number(element.clientHeight || 0));
    syncViewportHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => syncViewportHeight());
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", syncViewportHeight);
    return () => {
      window.removeEventListener("resize", syncViewportHeight);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [tab]);

  useEffect(() => {
    return () => {
      if (userTableScrollRafRef.current) {
        window.cancelAnimationFrame(userTableScrollRafRef.current);
        userTableScrollRafRef.current = 0;
      }
    };
  }, []);

  const userSearchHaystack = (row = {}) =>
    normalize(
      [
        row?.email,
        row?.id,
        row?.bidderName,
        row?.username,
        row?.company,
        row?.phone,
        row?.auctionStatus,
        row?.lastAuctionName,
        row?.myAuctionsRegistrationCount,
        row?.myAuctionsUniqueBidCount,
        row?.totalRegistrationsLast12Months,
        row?.totalUniqueBidCountLast12Months,
        row?.lastEmailSentAt,
        row?.customerTypeManual,
        row?.customerType,
        row?.buyerTier,
        row?.accountManagerId,
        row?.defaultPickupLocationName,
        row?.defaultPickupTime,
        row?.overallHammerPriceValue,
        row?.overallHammerPrice,
        row?.totalHighBid,
        row?.lcv?.estimate,
        row?.derived?.lcvEstimate,
        row?.derived?.bpi90,
        row?.derived?.resellerPotentialScore90,
        row?.rolling?.win90?.totalBidsPlaced,
        row?.win90?.totalBidsPlaced,
        row?.rolling?.win90?.bidIntensity,
        row?.win90?.bidIntensity,
        row?.rolling?.win90?.bvi,
        row?.win90?.bvi,
      ]
        .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
        .join(" ")
    );

  const getTierRank = (row = {}) => {
    const tier = normalize(getEffectiveTier(row));
    if (tier === "new") return 0;
    if (tier === "elite") return 1;
    if (tier === "power") return 2;
    if (tier === "emerging") return 3;
    if (tier === "casual") return 4;
    return 99;
  };

  const userMatchesCurrentFilters = (row = {}) => {
    const searchValue = normalize(search);
    const tierValue = normalize(tierFilter || "all");
    const regsThresholdValue = String(regsThresholdFilter || "all").trim();
    const lastEmailValue = normalize(lastEmailFilter || "all");
    const customerTypeValue = String(customerTypeFilter || "all").trim();
    const accountManagerValue = normalizeAccountManagerId(accountManagerFilter || "all");
    const regsCount = getRegsCount(row);
    const regs12mCount = getRegs12mCount(row);
    const lastEmailMs = safeToMillis(row?._lastEmailSentAtMs ?? row?.lastEmailSentAt);

    if (regsThresholdValue !== "all") {
      const threshold = Number(regsThresholdValue);
      if (!(regsCount > 0)) return false;
      if (Number.isFinite(threshold) && regsCount > threshold) return false;
    }
    if (lastEmailValue !== "all") {
      if (lastEmailValue === "never") {
        if (lastEmailMs) return false;
      } else if (lastEmailValue === "sent") {
        if (!lastEmailMs) return false;
      } else {
        const days = Number(String(lastEmailValue).replace("days:", ""));
        if (!lastEmailMs) return false;
        if (Number.isFinite(days) && days > 0) {
          const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
          if (lastEmailMs < cutoffMs) return false;
        }
      }
    }
    if (tierValue !== "all") {
      if (normalize(getEffectiveTier(row)) !== tierValue) return false;
      if (tierValue === "new") {
        if (newTierSubFilter === "new_to_hibid_and_af") {
          if (!(regsCount === 1 && regs12mCount === 1)) return false;
        } else if (newTierSubFilter === "new_to_af") {
          if (regsCount !== 1) return false;
        }
      }
    }
    if (customerTypeValue !== "all") {
      const wantedType = normalizeCustomerTypeKey(customerTypeValue);
      const rowType = getCustomerType(row);
      if (wantedType && rowType !== wantedType) return false;
    }
    if (accountManagerValue && accountManagerValue !== "all") {
      const rowManagerId = getResolvedAccountManagerIdForUser(row);
      if (rowManagerId !== accountManagerValue) return false;
    }
    if (searchValue) {
      const haystack = userSearchHaystack(row);
      if (!haystack.includes(searchValue)) return false;
    }
    return true;
  };

  const getUserSortValue = (row = {}, sortKey = "updatedAt") => {
    switch (sortKey) {
      case "bidderName":
        return normalize(row?.bidderName || "");
      case "email":
        return normalize(row?.email || row?.id || "");
      case "customerType":
        return normalize(getCustomerType(row));
      case "tier":
        return getTierRank(row);
      case "overallHammer":
        return safeNum(getOverallHammerPrice(row) ?? 0);
      case "lcv":
        return safeNum(getLcvEstimate(row) ?? 0);
      case "bpi90":
        return getBpi90(row);
      case "lots90":
        return safeNum(getLotsWonFromWin(getRollingWin(row, "win90")) ?? 0);
      case "hibidBids90":
        return getHibidBids90Count(row);
      case "bidIntensity90":
        return getBidIntensity90(row);
      case "regs":
        return getRegsCount(row);
      case "bids":
        return getBidsCount(row);
      case "regs12m":
        return getRegs12mCount(row);
      case "bids12m":
        return toAbsInt(row?.totalUniqueBidCountLast12MonthsValue ?? row?.totalUniqueBidCountLast12Months ?? row?.bids12m ?? 0);
      case "lastEmailSentAt":
        return safeToMillis(row?._lastEmailSentAtMs ?? row?.lastEmailSentAt) ?? 0;
      case "updatedAt":
      default:
        return safeToMillis(row?.updatedAt) ?? 0;
    }
  };

  const allFilteredUsers = useMemo(() => {
    const baseRows = Array.isArray(userRows) ? userRows : [];
    const filteredRows = baseRows.filter((row) => userMatchesCurrentFilters(row));
    const dir = String(userSortDir || "desc").toLowerCase() === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = getUserSortValue(a, userSortKey || "updatedAt");
      const bv = getUserSortValue(b, userSortKey || "updatedAt");
      if (typeof av === "number" && typeof bv === "number") {
        if (av === bv) return normalize(a?.email || a?.id || "").localeCompare(normalize(b?.email || b?.id || "")) * dir;
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [userRows, search, tierFilter, newTierSubFilter, regsThresholdFilter, lastEmailFilter, customerTypeFilter, accountManagerFilter, userSortKey, userSortDir]);

  const userVirtualizedWindow = useMemo(() => {
    const total = Array.isArray(allFilteredUsers) ? allFilteredUsers.length : 0;
    if (!total) {
      return {
        rows: [],
        startIndex: 0,
        endIndex: 0,
        topSpacerHeight: 0,
        bottomSpacerHeight: 0,
      };
    }
    const safeViewportHeight = Math.max(320, Number(userTableViewportHeight || 0));
    const visibleCount = Math.max(1, Math.ceil(safeViewportHeight / userTableRowHeight));
    const rawStart = Math.floor(Math.max(0, Number(userTableScrollTop || 0)) / userTableRowHeight);
    const startIndex = Math.max(0, Math.min(total - 1, rawStart - userTableOverscanRows));
    const endIndex = Math.min(total, startIndex + visibleCount + userTableOverscanRows * 2);
    return {
      rows: allFilteredUsers.slice(startIndex, endIndex),
      startIndex,
      endIndex,
      topSpacerHeight: startIndex * userTableRowHeight,
      bottomSpacerHeight: Math.max(0, (total - endIndex) * userTableRowHeight),
    };
  }, [allFilteredUsers, userTableScrollTop, userTableViewportHeight, userTableOverscanRows, userTableRowHeight]);

  const visibleUsers = userVirtualizedWindow.rows;
  const userTableTopSpacerHeight = userVirtualizedWindow.topSpacerHeight;
  const userTableBottomSpacerHeight = userVirtualizedWindow.bottomSpacerHeight;
  const usersTotalHeaderText = `${formatNumber2(allFilteredUsers.length)} filtered`;

  const selectableFilteredUserEmails = useMemo(
    () => (allFilteredUsers || []).map((row) => normalize(row?.email || row?.id)).filter((email) => isEmail(email)),
    [allFilteredUsers, isEmail, normalize]
  );

  const toggleSelectUser = (row) => {
    const email = getUserEmailLower(row);
    if (!isEmail(email)) return;
    setUserSelected((prev) => {
      const next = new Set(prev || []);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleSelectAllUsers = () => {
    if (!selectableFilteredUserEmails.length) return;
    setUserSelected((prev) => {
      const next = new Set(prev || []);
      const allSelected = selectableFilteredUserEmails.every((email) => next.has(email));
      if (allSelected) selectableFilteredUserEmails.forEach((email) => next.delete(email));
      else selectableFilteredUserEmails.forEach((email) => next.add(email));
      return next;
    });
  };

  const selectedFilteredUsers = useMemo(() => {
    const selected = userSelected || new Set();
    return (allFilteredUsers || []).filter((row) => selected.has(normalize(row?.email || row?.id)));
  }, [allFilteredUsers, userSelected, normalize]);

  const allFilteredUsersSelected =
    selectableFilteredUserEmails.length > 0 && selectableFilteredUserEmails.every((email) => userSelected?.has?.(email));
  const someFilteredUsersSelected = selectableFilteredUserEmails.some((email) => userSelected?.has?.(email));

  useEffect(() => {
    const visible = new Set((allFilteredUsers || []).map((row) => normalize(row?.email || row?.id)).filter((email) => isEmail(email)));
    setUserSelected((prev) => {
      const prevSet = prev || new Set();
      const next = new Set([...prevSet].filter((email) => visible.has(email)));
      if (next.size === prevSet.size) return prevSet;
      return next;
    });
  }, [allFilteredUsers, isEmail, normalize]);

  const updateCustomerTypeForUser = async (row, nextTypeRaw) => {
    const email = normalize(row?.email || row?.id);
    if (!isEmail(email)) {
      toastOpen("Missing or invalid user email.", "error");
      return;
    }

    const nextType = String(nextTypeRaw || "").trim();
    const currentType = String(getCustomerType(row) || "").trim();
    if (nextType === currentType) return;

    setCustomerTypeSavingByEmail((prev) => ({ ...prev, [email]: true }));
    try {
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.usersUpdate}`, {
        method: "POST",
        body: JSON.stringify({
          email,
          updates: {
            customerType: nextType || null,
            customerTypeManual: nextType || null,
          },
        }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);

      applyUserPatchLocally(email, {
        customerType: nextType || null,
        customerTypeManual: nextType || null,
        updatedAt: new Date().toISOString(),
      });
      toastOpen("Customer type updated.", "success");
    } catch (error) {
      toastOpen(error?.message || "Failed to update customer type.", "error");
    } finally {
      setCustomerTypeSavingByEmail((prev) => {
        const next = { ...prev };
        delete next[email];
        return next;
      });
    }
  };

  const updateAccountManagerForUser = async (row, nextManagerIdRaw) => {
    const email = normalize(row?.email || row?.id);
    if (!isEmail(email)) {
      toastOpen("Missing or invalid user email.", "error");
      return;
    }

    const nextManagerId = normalizeAccountManagerId(nextManagerIdRaw || "");
    const currentManagerId = getResolvedAccountManagerIdForUser(row);
    if (nextManagerId === currentManagerId) return;

    setAccountManagerSavingByEmail((prev) => ({ ...prev, [email]: true }));
    try {
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.usersUpdate}`, {
        method: "POST",
        body: JSON.stringify({
          email,
          updates: {
            accountManagerId: nextManagerId || null,
          },
        }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);

      applyUserPatchLocally(email, {
        accountManagerId: nextManagerId || "",
        updatedAt: new Date().toISOString(),
      });
      toastOpen("Account manager updated.", "success");
    } catch (error) {
      toastOpen(error?.message || "Failed to update account manager.", "error");
    } finally {
      setAccountManagerSavingByEmail((prev) => {
        const next = { ...prev };
        delete next[email];
        return next;
      });
    }
  };

  const showUsersLoadingModal = tab === 0 && !needsSubscriptionLink && (usersInitialLoading || usersTableLoading);

  return {
    search,
    setSearch,
    tierFilter,
    setTierFilter,
    newTierSubFilter,
    setNewTierSubFilter,
    regsThresholdFilter,
    setRegsThresholdFilter,
    lastEmailFilter,
    setLastEmailFilter,
    customerTypeFilter,
    setCustomerTypeFilter,
    accountManagerFilter,
    setAccountManagerFilter,
    userRows,
    setUserRows,
    userTableContainerRef,
    userSelected,
    userSortKey,
    setUserSortKey,
    userSortDir,
    setUserSortDir,
    userHasMore,
    userLoadingMore,
    usersLoadedOnce,
    usersInitialLoading,
    usersTableLoading,
    customerTypeSavingByEmail,
    accountManagerSavingByEmail,
    usersSearchInputRef,
    showUsersLoadingModal,
    fetchUsers,
    invalidateUsersCache,
    applyUsersSearch,
    clearUsersSearch,
    reloadUsersFromServer,
    onUsersTableScroll,
    allFilteredUsers,
    visibleUsers,
    userTableTopSpacerHeight,
    userTableBottomSpacerHeight,
    usersTotalHeaderText,
    getUserEmailLower,
    getOverallHammerPrice,
    applyUserPatchLocally,
    toggleSelectUser,
    toggleSelectAllUsers,
    selectedFilteredUsers,
    allFilteredUsersSelected,
    someFilteredUsersSelected,
    updateCustomerTypeForUser,
    updateAccountManagerForUser,
  };
}
